<template>
  <div class="p-6 dark:text-gray-50" v-if="canAdd || user?.is_staff">
    <div class="flex space-x-3 mb-6">
      <button
        @click="goBack"
        class="bg-gray-600 text-white px-4 py-1.5 rounded-md hover:bg-gray-700 text-sm flex items-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
        </svg>
        Back
      </button>
      <h1 class="text-2xl font-semibold dark:text-white">{{ isEditMode ? 'Edit' : 'Add' }} Endpoint Test Job</h1>
    </div>

    <!-- Page-level Error Banner -->
    <div v-if="pageError" class="bg-red-50 dark:bg-red-900/20 border border-red-400 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4 flex items-center">
      <svg class="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
      </svg>
      <span class="font-medium">{{ pageError }}</span>
    </div>

    <form @submit.prevent="submitForm" class="space-y-4 bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <JobDescriptionInput v-model="formData.job_description" :error="fieldErrors.job_description" :job-name="jobName" />

      <!-- Module -->
      <div class="flex flex-col">
        <label for="module" class="mb-1 font-medium">
          Module<span class="text-red-500">*</span>
        </label>
        <GlassSelect
          v-model="formData.module"
          :options="moduleOptions"
          valueKey="value"
          labelKey="label"
          placeholder="Select module"
          :error="!!fieldErrors.module"
          @change="clearTargets"
        />
        <p class="text-sm text-gray-500 mt-1">{{ moduleHint }}</p>
        <FieldError :message="fieldErrors.module" />
      </div>

      <!-- Targets -->
      <div class="flex flex-col">
        <label for="targets" class="mb-1 font-medium">
          Targets<span class="text-red-500">*</span>
        </label>
        <input
          id="targets"
          v-model="targetInput"
          type="text"
          :placeholder="targetPlaceholder"
          :class="inputClass('targets')"
        />
        <FieldError :message="fieldErrors.targets" />
        <div class="flex flex-wrap gap-2 mt-2">
          <span
            v-for="target in targets"
            :key="target"
            class="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-sm flex items-center"
          >
            {{ target }}
            <button type="button" @click="removeTarget(target)" class="ml-2 hover:text-blue-500">x</button>
          </span>
        </div>
        <button
          type="button"
          @click="addTarget"
          :disabled="!targetInput"
          :class="[
            'mt-2 px-3 py-1 rounded text-sm w-fit',
            targetInput ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          ]"
        >
          Add Target
        </button>
      </div>

      <!-- Scrape Settings -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col">
          <label for="scrape_interval" class="mb-1 font-medium">Scrape Interval (seconds)</label>
          <input
            id="scrape_interval"
            v-model.number="formData.scrape_interval"
            type="number"
            min="10"
            class="border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div class="flex flex-col">
          <label for="scrape_timeout" class="mb-1 font-medium">Scrape Timeout (seconds)</label>
          <input
            id="scrape_timeout"
            v-model.number="formData.scrape_timeout"
            type="number"
            min="5"
            class="border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end space-x-2 pt-4">
        <button
          type="button"
          @click="goBack"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {{ isEditMode ? 'Update' : 'Create' }} Endpoint Test Job
        </button>
      </div>
    </form>
  </div>

  <PageError v-else-if="permsLoaded" :error="'Permission denied'" :errorCode="'403'" />
</template>

<script>
import { createAlloyBlackbox, getAlloyBlackboxById, updateAlloyBlackbox } from "@/services/alloyService";
import FieldError from "@/components/FieldError.vue";
import JobDescriptionInput from "@/components/JobDescriptionInput.vue";
import PageError from "@/components/PageError.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "AddAlloyBlackbox",
  components: { FieldError, JobDescriptionInput, PageError, GlassSelect },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      fieldErrors: {},
      pageError: null,
      jobName: "",
      formData: {
        job_description: "",
        module: "http_2xx",
        scrape_interval: 60,
        scrape_timeout: 30,
      },
      targets: [],
      targetInput: "",
      canAdd: false,
      isLoading: false,
      moduleOptions: [
        { value: "http_2xx", label: "HTTP Response Times" },
        { value: "tcp_connect", label: "3WHS Response Times" },
        { value: "icmp", label: "ICMP Response Times" },
        { value: "dns", label: "DNS Response Times" },
      ],
    };
  },
  computed: {
    currentSchema() { return this.$route.query.schemaName || this.tenant.currentSchemaName?.value || null; },
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    configId() {
      return this.$route.params.configId;
    },
    jobId() {
      return this.$route.params.jobId;
    },
    isEditMode() {
      return !!this.jobId;
    },
    moduleHint() {
      const hints = {
        http_2xx: "Checks HTTP/HTTPS endpoints for 2xx response codes",
        tcp_connect: "Tests TCP port connectivity (requires host:port format)",
        icmp: "Performs ICMP ping tests",
        dns: "Tests DNS resolution",
      };
      return hints[this.formData.module] || "";
    },
    targetPlaceholder() {
      const placeholders = {
        http_2xx: "https://example.com",
        tcp_connect: "example.com:443",
        icmp: "192.168.1.1",
        dns: "example.com",
      };
      return placeholders[this.formData.module] || "Enter target";
    },
    autoGeneratedJobName() {
      if (this.isEditMode) {
        return "Auto-generated based on entity";
      }
      return "Will be generated after creation";
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    await this.tenant.loadInitialData(() => Promise.resolve());
    await this.checkPermissions();
    if (this.isEditMode) {
      await this.loadExistingJob();
    }
  },
  methods: {
    async checkPermissions() {
      if (this.user) {
        this.canAdd = await useStore.hasPermission("Can add Alloy Blackbox Job");
      }
    },
    async loadExistingJob() {
      this.isLoading = true;
      try {
        const job = await getAlloyBlackboxById(this.jobId, this.currentSchema);
        this.jobName = job.job_name;
        this.formData.job_description = job.job_description || "";
        this.formData.module = job.module;
        this.formData.scrape_interval = job.scrape_interval || 60;
        this.formData.scrape_timeout = job.scrape_timeout || 30;
        this.targets = job.targets || [];
      } catch (error) {
        console.error("Error loading Blackbox job:", error);
        alert("Failed to load job data");
        this.goBack();
      } finally {
        this.isLoading = false;
      }
    },
    inputClass(field) {
      return [
        'border rounded px-3 py-2 focus:outline-none focus:ring-2 dark:bg-gray-600',
        this.fieldErrors[field] ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-300 focus:ring-blue-500'
      ];
    },
    clearTargets() {
      this.targets = [];
      this.targetInput = "";
    },
    addTarget() {
      this.fieldErrors.targets = null;

      // Validate based on module type
      if (this.formData.module === "http_2xx") {
        if (!this.targetInput.startsWith("http://") && !this.targetInput.startsWith("https://")) {
          this.fieldErrors.targets = "HTTP targets must start with http:// or https://";
          return;
        }
      } else if (this.formData.module === "tcp_connect") {
        const portMatch = this.targetInput.match(/:(\d+)$/);
        if (!portMatch) {
          this.fieldErrors.targets = "TCP targets must include a port (e.g., example.com:443)";
          return;
        }
      }

      if (this.targetInput && !this.targets.includes(this.targetInput)) {
        this.targets.push(this.targetInput);
        this.targetInput = "";
      }
    },
    removeTarget(target) {
      this.targets = this.targets.filter((t) => t !== target);
    },
    goBack() {
      this.$router.push({ name: "AlloyDetails", params: { id: this.configId }, query: { schemaName: this.currentSchema, tab: "blackbox" } });
    },
    async submitForm() {
      this.pageError = null;
      this.fieldErrors = {};

      if (this.targets.length === 0) {
        this.fieldErrors.targets = "At least one target is required";
        return;
      }

      const data = {
        alloy_config: parseInt(this.configId),
        job_description: this.formData.job_description.trim(),
        module: this.formData.module,
        targets: this.targets,
        scrape_interval: this.formData.scrape_interval,
        scrape_timeout: this.formData.scrape_timeout,
      };

      try {
        if (this.isEditMode) {
          await updateAlloyBlackbox(this.jobId, data, this.currentSchema);
        } else {
          await createAlloyBlackbox(data, this.currentSchema);
        }
        this.goBack();
      } catch (error) {
        this.pageError = null;
        this.fieldErrors = {};

        const errData = error?.response?.data;
        if (errData) {
          // Handle field-specific errors
          Object.keys(errData).forEach((key) => {
            const errorMsg = Array.isArray(errData[key]) ? errData[key][0] : errData[key];
            this.fieldErrors[key] = errorMsg;
          });

          // Show page-level error for non-field errors or alloy_config errors
          // Replace technical backend error messages with user-friendly versions
          if (errData.alloy_config || errData.non_field_errors) {
            let errorMessage = errData.alloy_config?.[0] || errData.alloy_config ||
                               errData.non_field_errors?.[0] || errData.non_field_errors;

            // Replace technical error with client-friendly message
            if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('mimir')) {
              errorMessage = "Your organization tenant is not configured. Please contact Splitpoint support for assistance.";
            }

            this.pageError = errorMessage || "Please correct the errors below";
          } else if (Object.keys(errData).length > 0) {
            this.pageError = "Please correct the errors below";
          }
        } else {
          // Generic error message for network/server errors
          this.pageError = `Failed to ${this.isEditMode ? 'update' : 'create'} job. Please try again.`;
        }

        console.error(`Error ${this.isEditMode ? 'updating' : 'creating'} Blackbox job:`, error);
      }
    },
  },
};
</script>
